import { Flag, type ComboboxOptions, type CountryCode, type Item } from "@wayflyer/flyui";

export const COMMON_COUNTRY_CODES: CountryCode[] = ["US", "GB", "CA", "DE", "AU"];

// flyui's Flag component doesn't export its supported-codes allowlist at runtime, and it's grown
// to cover nearly every country - tracking the few gaps is far less likely to drift stale than
// mirroring the (large, growing) full allowlist. Re-check against flyui's Flag source if a flag
// unexpectedly falls back to "Globe".
const FLAG_UNSUPPORTED_CODES = new Set(["BQ", "XK"]);

const COUNTRY_KEYWORDS: Partial<Record<CountryCode, string[]>> = {
  US: ["America", "USA"],
  GB: ["UK", "Britain"],
};

export const enrichCountryOptions = (options: Item<CountryCode>[]): Item<CountryCode>[] =>
  options.map((option) => ({
    ...option,
    icon: <Flag code={FLAG_UNSUPPORTED_CODES.has(option.id) ? "Globe" : option.id} />,
    keywords: COUNTRY_KEYWORDS[option.id] ?? option.keywords,
  }));

// Groups already-enriched options into a "Suggested" section (in commonCodes' own priority order,
// not alphabetical) followed by the rest under "All countries".
export const groupCommonCountryOptions = (
  options: Item<CountryCode>[],
  commonCodes: CountryCode[] = COMMON_COUNTRY_CODES
): ComboboxOptions<CountryCode> => {
  const commonCodesSet = new Set(commonCodes);
  const commonItems = commonCodes
    .map((code) => options.find((option) => option.id === code))
    .filter((option): option is Item<CountryCode> => option != null);
  const restItems = options.filter((option) => !commonCodesSet.has(option.id));

  return [
    // flyui's grouped-option `id` is declared but unread by AutocompleteField's renderer (sections
    // are keyed by `name`) - these placeholders satisfy the CountryCode-typed field without implying
    // either group is itself a country.
    { name: "Suggested", id: "suggested" as CountryCode, items: commonItems },
    { name: "All countries", id: "all" as CountryCode, items: restItems },
  ];
};
